// =============================================================================
// supplier-client.ts — Client API référentiel fournisseur ADMIN (A-03/04/05), DOUBLABLE.
// =============================================================================
// Miroir des endpoints ADMIN de D5 (apps/api/src/supplier-reference/
// supplier-reference-admin.controller.ts). Comme les autres clients UI (legal,
// parameters, identity), expose une INTERFACE (SupplierClient) consommée par les
// hooks TanStack ; l'impl HTTP réelle (fetch) viendra derrière la MÊME interface
// sans toucher l'UI — concern transverse HORS D6 (GATE_0 D6 b.2 : stub-first).
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : les enums sont
// répliqués en union de littéraux ASCII (parité Prisma EstablishmentState /
// ContactState / ContactRole). La SPA ne couple PAS au paquet DB.
//
// MARQUEURS DIFFÉRÉS (NE PAS inventer de données — GATE_0 D6) :
//   - signatureCgu (A-03)       : composée hors-read-model, endpoint admin
//                                 cross-fournisseur ABSENT S5 → différé-visible.
//   - participations (A-03/04)  : BC campaign ABSENT S5 → différé.
//   - dossier === null (A-04)   : mode dégradé fail-open (ACL D5) → saisie manuelle.
//   - bodaccVigilance (A-04)    : niveaux 2/3 BODACC → différé S6.

import { HttpApiError } from '@/api/http-error';

// --- Enums miroirs HTTP (parité Prisma, ASCII — accents = i18n front) ---

/** État établissement (6 états, parité Prisma EstablishmentState). */
export type EstablishmentStateDto =
  | 'Brouillon'
  | 'Soumis'
  | 'Refuse'
  | 'Actif'
  | 'Suspendu'
  | 'Archive';

/** Rôle d'un contact (parité Prisma ContactRole). */
export type ContactRoleDto = 'MASTER' | 'COLLABORATEUR';

/** État d'un contact (parité Prisma ContactState). */
export type ContactStateDto = 'Invite' | 'Actif' | 'Inactif' | 'ResignatureRequise';

/**
 * Statut d'activation DÉRIVÉ du lien (miroir HTTP de `TokenActivationStatus`, ActivationStatusService).
 * Renfort TEXTE en UI (« Envoyé / Expiré / Activé »), jamais une couleur seule. `null` = aucune
 * identité émise pour ce contact (hors périmètre / dérivation fail-closed) — l'UI affiche « — ».
 */
export type TokenActivationStatusDto = 'Envoye' | 'Expire' | 'Active';

/** Statut du lien d'activation d'un interlocuteur (miroir de `TokenStatusView`). */
export interface ContactTokenStatusDto {
  readonly contactId: string;
  readonly status: TokenActivationStatusDto | null;
}

/**
 * T6-TOKENS (§2.2) — vue « Accès & tokens » ENRICHIE d'un interlocuteur (miroir HTTP de la vue back
 * `TokenAccessView`, ActivationStatusService, READ-ONLY). Projette l'état EXISTANT en base — AUCUNE
 * écriture. Par rapport à `ContactTokenStatusDto` (statut seul, colonne du tableau), cette vue porte
 * les DATES réelles du dernier token + le COMPTEUR de renvoi vivant + les PLAFONDS de politique.
 *
 * `contactId` = clé de corrélation (parité `ContactTokenStatusDto`, non projetée par la carte).
 * `issuedAt`/`expiresAt` = ISO 8601 `string | null` (la SPA ne couple pas au type `Date` du back,
 * parité `masterLastActivityAt`/`asOf`). `null` = aucun token émis / dérivation fail-closed cross-tenant.
 * `ttlHours` (72) et `resendDailyCap` (3) = PLAFONDS DE POLITIQUE servis par l'API (source unique
 * `SupplierIdentityConfig`, surchargeable par env) — le FRONT ne code AUCUNE constante 72/3 en dur.
 * `resendCount` = N renvois observés sur la fenêtre 24 h glissante.
 *
 * SECOPS : ne projette JAMAIS hash/secret/email/`requestedFromIp` (IP source, PII) — parité P-5/R-3.
 */
export interface ContactTokenAccessDto {
  readonly contactId: string;
  readonly status: TokenActivationStatusDto | null;
  readonly issuedAt: string | null;
  readonly expiresAt: string | null;
  readonly ttlHours: number;
  readonly resendCount: number;
  readonly resendDailyCap: number;
}

/** Source d'un snapshot d'enrichissement (parité ACL EnrichmentSource). */
export type EnrichmentSourceDto = 'DataGouv' | 'BODACC';

/** Provenance d'un snapshot (parité ACL EnrichmentProvenance). */
export type EnrichmentProvenanceDto = 'enrichi' | 'saisie_manuelle' | 'perime';

/**
 * Raison du mode dégradé fail-open (miroir HTTP de l'enum ACL `EnrichmentDegradedReason`,
 * valeurs identiques au Published Language). Exposée pour l'observabilité / l'UX A-04 ;
 * le consommateur bascule en saisie manuelle quel que soit le motif.
 */
export type EnrichmentDegradedReasonDto =
  | 'timeout'
  | 'unreachable'
  | 'http_error'
  | 'not_found';

/** Adresse structurée du siège (miroir VO Address ; tous nullable). */
export interface EnrichmentAddressDto {
  readonly voie: string | null;
  readonly codePostal: string | null;
  readonly commune: string | null;
  readonly ligneComplete: string | null;
}

/**
 * Dossier d'enrichissement socle DataGouv (miroir EnrichmentSnapshot, niveau 1).
 * Niveaux 2/3 BODACC NON modélisés (différé S6) — aucun champ inventé.
 */
export interface EnrichmentSnapshotDto {
  readonly snapshotId: string;
  readonly targetSiret: string;
  readonly source: EnrichmentSourceDto;
  readonly provenance: EnrichmentProvenanceDto;
  /** Date civile 'YYYY-MM-DD' (as-of, semaine de validité). */
  readonly asOf: string;
  readonly raisonSocialeLegale: string | null;
  readonly codeNaf: string | null;
  readonly adresseSiege: EnrichmentAddressDto | null;
}

// --- A-03 (liste) — miroir AdminListView (read-model D5) ---

/**
 * Ligne du read-model A-03 (liste admin). Champs OWNED supplier-reference.
 * `signatureCgu` / `participations` NE SONT PAS dans le read-model D5 : ils sont
 * gérés comme marqueurs différés au niveau de l'UI (PAS de champ inventé ici).
 */
export interface AdminListItemDto {
  readonly siren: string;
  readonly raisonSocialeLegale: string | null;
  readonly siret: string;
  readonly etablissementState: EstablishmentStateDto;
  /**
   * T2-REGISTRE — statut d'activation DÉRIVÉ SERVEUR du MASTER de la ligne (ActivationStatusService,
   * fail-closed cross-tenant). `null` = aucune identité MASTER émise / hors périmètre. Le FRONT rend
   * l'ICÔNE HORLOGE (doublée d'un LIBELLÉ texte, WCAG) ssi `activationStatus === 'Expire'`.
   */
  readonly activationStatus: TokenActivationStatusDto | null;
  /**
   * M2 — dernière activité du MASTER (SERVIE par le read-model admin A-03,
   * `AdminListView.masterLastActivityAt`, projection DE.AccessGranted / colonne last_activity_at).
   * Miroir HTTP d'un `Date | null` back : horodatage ISO 8601 `string`, `null` tant qu'aucun accès
   * n'a été observé. La SPA ne couple pas au type `Date` du Published Language (parité EnrichmentSnapshotDto.asOf).
   */
  readonly masterLastActivityAt: string | null;
  /**
   * DBU §2.2 l.676 « date de dernière connexion » — donnée RÉELLE (A2, batch gelé #1) :
   * `AdminListView.masterLastLoginAt` = MAX(started_at) de la session du MASTER (LastConnectionService,
   * dérivé read-time in-process, DISPLAY-only, non SQL-triable). ISO 8601 `string` | `null` si jamais
   * connecté. Remplace à l'affichage l'ancien placeholder `masterLastActivityAt` (mort/NULL en prod).
   */
  readonly masterLastLoginAt: string | null;
  /**
   * S29-CGU-NONSIGN (§2.9) — nom complet « nom prénom » du MASTER le + récemment actif (PII,
   * admin-only ; miroir de `AdminListView.masterNomComplet`, LATERAL array_agg[1] côté API). `null`
   * si aucun MASTER. Consommé par la vue NON-SIGNATAIRES (merge FRONT par SIRET). L'EXPORT CSV le
   * laisse null (sans PII).
   */
  readonly masterNomComplet: string | null;
  /**
   * S29-CGU-NONSIGN (§2.9) — email du MASTER le + récemment actif (PII, admin-only ; miroir de
   * `AdminListView.masterEmail`). `null` si aucun MASTER. Même usage que `masterNomComplet`.
   */
  readonly masterEmail: string | null;
}

// --- T2-REGISTRE (A-03) — recherche / filtres / tri / pagination SERVEUR ------

/**
 * Colonne de tri du registre — colonnes OWNED supplier-reference UNIQUEMENT (le serveur ne trie pas
 * `participations`/`derniereDateReferencement`, composées côté UI). Allowlist stricte côté API.
 */
export type AdminListSortColumn =
  | 'siren'
  | 'raisonSocialeLegale'
  | 'siret'
  | 'etablissementState'
  | 'masterLastActivityAt';

/** Direction de tri (allowlist stricte côté API). */
export type AdminListSortDirection = 'asc' | 'desc';

/**
 * Critères de lecture du registre A-03 (query params `GET /admin/establishments`). Tous OPTIONNELS ;
 * les défauts (`sort=siret`, `dir=asc`, `page=1`, `pageSize=25`) sont l'autorité de l'API. `sirets`
 * (≤ 500) est le filtre « campagne » RÉSOLU côté UI (opaque côté serveur). `from`/`to` = bornes ISO
 * `YYYY-MM-DD` sur `masterLastActivityAt` (GATE B). `state` = filtre statut d'établissement (GATE A).
 * `tokenStatus` = filtre STATUT D'ACTIVATION (multi ∈ {Active,Envoye,Expire}) : le FRONT n'envoie QUE
 * ce param compact, la COMPOSITION est SERVEUR (le back résout en contactIds — jamais le front).
 */
export interface AdminListQueryDto {
  readonly q?: string;
  readonly state?: readonly EstablishmentStateDto[];
  readonly from?: string;
  readonly to?: string;
  readonly sirets?: readonly string[];
  readonly tokenStatus?: readonly TokenActivationStatusDto[];
  readonly sort?: AdminListSortColumn;
  readonly dir?: AdminListSortDirection;
  readonly page?: number;
  readonly pageSize?: number;
}

/**
 * Enveloppe paginée du registre A-03 (miroir `AdminListPageView`). `total` = compte APRÈS filtres,
 * AVANT pagination ; `page`/`pageSize` = écho des params normalisés par l'API.
 */
export interface AdminListPageDto {
  readonly items: readonly AdminListItemDto[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// --- A-04 (fiche) — miroir AdminEstablishmentView (read-model D5) ---

/** Interlocuteur de la fiche A-04 (sous-ensemble Contact OWNED). */
export interface AdminContactDto {
  readonly contactId: string;
  /**
   * M2 — nom complet « nom prénom » du contact (identité nominative, PII). SERVI par le read-model
   * admin A-04 (`AdminContactView.nomComplet` = `${nom} ${prenom}`).
   */
  readonly nomComplet: string;
  readonly email: string;
  readonly role: ContactRoleDto;
  readonly state: ContactStateDto;
}

/** Fiche A-04 (admin). */
export interface AdminEstablishmentDto {
  readonly siren: string;
  readonly raisonSocialeLegale: string | null;
  readonly siret: string;
  readonly etablissementState: EstablishmentStateDto;
  /** Dossier socle ACL D4 (null si dégradé / introuvable — fail-open A-04). */
  readonly dossier: EnrichmentSnapshotDto | null;
  readonly interlocuteurs: readonly AdminContactDto[];
  /** MARQUEUR DIFFÉRÉ : participations campagne (BC campaign ABSENT S5). */
  readonly participations: null;
}

// --- Corps des commandes (le SIRET vient TOUJOURS de l'URL, jamais du body) ---

/** Corps A-05 ACTE 1 (créer le fournisseur). */
export interface EnrollInput {
  readonly masterEmail: string;
  /** Nom de famille du contact MASTER désigné (M2, requis NOT NULL côté API). */
  readonly masterNom: string;
  /** Prénom du contact MASTER désigné (M2, requis NOT NULL côté API). */
  readonly masterPrenom: string;
}

/** Résultat de l'acte 1 (id du contact MASTER créé). */
export interface EnrollResult {
  readonly masterContactId: string;
}

/** Corps A-05 ACTE 2 (envoyer l'invitation). */
export interface InvitationInput {
  /** Dérogation domaine responsabilisée (true = enrôlement hors-domaine assumé). */
  readonly domainOverride?: boolean;
}

/** Corps révocation d'un contact (admin). */
export interface RevokeContactInput {
  readonly contactId: string;
  readonly targetKind: 'master' | 'collaborateur';
  readonly reason: string;
}

/**
 * Corps AJOUT d'un contact COLLABORATEUR PAR l'ADMIN (DEC-18 Option A). Le SIRET vient TOUJOURS de
 * l'URL (jamais du body). `domainOverride?` = dérogation domaine responsabilisée (parité EnrollInput/
 * InvitationInput) : true = ajout hors-domaine assumé. L'AUTORITÉ (mur domaine, émission d'event +
 * identité, cycle Invité→Actif) reste l'API — l'UI relaie l'intention.
 */
export interface AddContactInput {
  readonly email: string;
  readonly nom: string;
  readonly prenom: string;
  readonly domainOverride?: boolean;
}

/** Résultat de l'ajout d'un contact admin (id du COLLABORATEUR créé). */
export interface AddContactResult {
  readonly newContactId: string;
}

/** Corps transfert du rôle MASTER (admin). */
export interface TransferMasterInput {
  readonly fromContactId: string;
  readonly toContactId: string;
}

/**
 * Résultat d'un refresh d'enrichissement — UNION DISCRIMINÉE sur `status`, miroir HTTP fidèle
 * de l'union canonique du Published Language `EnrichmentResult`
 * (packages/contracts/src/acl/enrichment-lookup.port.ts). FAIL-OPEN :
 *
 * - `ok`       : snapshot frais disponible (`snapshot` TOUJOURS défini).
 * - `degraded` : la source n'a pas fourni de snapshot frais. Porte la `reason` ET, si connu, le
 *                DERNIER snapshot périmé (`stale`) — MÊME VO mirror `EnrichmentSnapshotDto`
 *                (provenance `perime`), JAMAIS un type parallèle. `stale` est OPTIONNEL (absent si
 *                aucun snapshot antérieur n'est connu). L'UX A-04 affiche le périmé APRÈS un
 *                avertissement.
 *
 * Écart assumé vs contrat : les dates VO (`asOf`) sont des `string` ISO côté HTTP UI (voir
 * EnrichmentSnapshotDto) — la SPA ne couple pas au type `Date` du Published Language.
 */
export type EnrichmentRefreshResult =
  | { readonly status: 'ok'; readonly snapshot: EnrichmentSnapshotDto }
  | {
      readonly status: 'degraded';
      readonly reason: EnrichmentDegradedReasonDto;
      readonly stale?: EnrichmentSnapshotDto;
    };

/**
 * Erreur applicative renvoyée par l'API admin (HTTP 4xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète jamais la règle métier (autorité = API).
 */
export class SupplierApiError extends HttpApiError {
  // `status` (hérité de HttpApiError) : statut HTTP d'origine quand connu. Sert à discriminer les
  // erreurs de renvoi d'activation PAR CODE (429/409/404), sans dépendre du message serveur brut.
  public constructor(message: string, status?: number) {
    super(message, status, 'SupplierApiError');
  }
}

/**
 * Contrat du client référentiel fournisseur ADMIN — miroir des endpoints D5.
 * Aucune règle métier ici : les transitions/validations sont l'autorité de l'API
 * (RBAC AdminSessionGuard, withTenant). L'UI relaie l'intention.
 */
export interface SupplierClient {
  // --- Lectures (A-03 / A-04, withAdmin côté API) ---

  /**
   * GET /admin/establishments — registre admin cross-fournisseur PAGINÉ (A-03 / T2-REGISTRE).
   * `query` porte recherche (`q`), filtres (`state`/`from`/`to`/`sirets`), tri (`sort`/`dir`) et
   * pagination (`page`/`pageSize`). Réponse = enveloppe `{ items, total, page, pageSize }`.
   */
  listEstablishments(query?: AdminListQueryDto): Promise<AdminListPageDto>;
  /** GET /admin/establishments/:siret — fiche admin (A-04). */
  getEstablishment(siret: string): Promise<AdminEstablishmentDto>;
  /**
   * GET /admin/establishments/:siret/contacts/token-status — statut du lien d'activation
   * (Envoyé/Expiré/Activé) des interlocuteurs de la fiche (withAdmin, dérivé côté identity).
   */
  listContactsTokenStatus(siret: string): Promise<readonly ContactTokenStatusDto[]>;
  /**
   * T6-TOKENS — GET /admin/establishments/:siret/contacts/token-access — vue « Accès & tokens »
   * ENRICHIE (statut + dates réelles + compteur/plafond de renvoi + fenêtre TTL) des interlocuteurs
   * de la fiche. READ-ONLY : projette l'état EXISTANT (withAdmin, dérivé côté identity), AUCUNE
   * écriture. Endpoint DISTINCT de `token-status` (zéro régression sur le registre T2-REGISTRE).
   */
  listContactsTokenAccess(siret: string): Promise<readonly ContactTokenAccessDto[]>;

  // --- A-05 : enrôlement en 2 ACTES DISTINCTS ---

  /** ACTE 1 — POST /admin/establishments/:siret/enroll (Brouillon, MASTER Invité, AUCUN event). */
  enroll(siret: string, input: EnrollInput): Promise<EnrollResult>;
  /** ACTE 2 — POST /admin/establishments/:siret/invitation (Brouillon→Soumis + DE.SupplierEnrolled). */
  sendInvitation(siret: string, input: InvitationInput): Promise<void>;

  // --- Transitions d'état (manuel ADMIN) ---
  //
  // T3-SUSPENSION : `suspend`/`refuse` portent un MOTIF (`reason`) OBLIGATOIRE — texte libre
  // légitimement dans le body (parité `RevokeContactInput.reason`), le SIRET reste dans l'URL
  // (invariant n°1). `reactivate`/`archive` NE portent PAS de reason (schémas contrat `.strict()`).
  // `adminId`/IP sont posés SERVEUR (jamais depuis le front). L'autorité (validité de la transition,
  // émission d'event, redaction PII du motif à l'audit) reste l'API.

  /** POST /admin/establishments/:siret/refuse (Soumis→Refuse) — MOTIF OBLIGATOIRE (body). */
  refuse(siret: string, reason: string): Promise<void>;
  /** POST /admin/establishments/:siret/suspend (Actif→Suspendu) — DESTRUCTIF + MOTIF OBLIGATOIRE (body). */
  suspend(siret: string, reason: string): Promise<void>;
  /** POST /admin/establishments/:siret/reactivate (Suspendu→Actif) — SANS motif. */
  reactivate(siret: string): Promise<void>;
  /** POST /admin/establishments/:siret/archive (no-DELETE) — DESTRUCTIF, SANS motif (confirmation UI). */
  archive(siret: string): Promise<void>;

  // --- Contacts (admin) ---

  /**
   * POST /admin/establishments/:siret/contacts/:contactId/resend-activation — renvoi du lien
   * d'activation à un interlocuteur NON activé (anti-abus 3/24h côté API). Erreurs par CODE :
   * 429 (rate-limit), 409 (déjà activé), 404 (contact absent/hors établissement).
   */
  resendContactActivation(siret: string, contactId: string): Promise<void>;
  /**
   * POST /admin/establishments/:siret/contacts — AJOUT d'un contact COLLABORATEUR PAR l'ADMIN
   * (DEC-18) sur un établissement existant, SANS session MASTER. Le contact naît « Invité » + reçoit
   * un lien d'activation ; une fois « Actif » (post-CGU), il devient promouvable MASTER (transfert
   * déjà livré). Distinct d'`enroll` : PAS de 409 « SIRET déjà présent » (add ≠ enroll).
   */
  addContact(siret: string, input: AddContactInput): Promise<AddContactResult>;
  /** POST /admin/establishments/:siret/contacts/revoke — DESTRUCTIF (confirmation UI). */
  revokeContact(siret: string, input: RevokeContactInput): Promise<void>;
  /** POST /admin/establishments/:siret/contacts/transfer-master — DESTRUCTIF (confirmation UI). */
  transferMaster(siret: string, input: TransferMasterInput): Promise<void>;

  // --- Enrichissement ---

  /** POST /admin/establishments/:siret/enrichment/refresh (fail-open dégradé → saisie manuelle). */
  refreshEnrichment(siret: string): Promise<EnrichmentRefreshResult>;
}
